import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Box,
  Button,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useMediaQuery,
  useTheme,
  Divider,
  Slide,
  useScrollTrigger,
  TextField,
  Menu,
  MenuItem,
  InputAdornment,
} from '@mui/material';

import HomeIcon from '@mui/icons-material/Home';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import GroupIcon from '@mui/icons-material/Group';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import MenuIcon from '@mui/icons-material/Menu';
import SearchIcon from '@mui/icons-material/Search';
import CampaignIcon from '@mui/icons-material/Campaign';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth'; // 🗓 Calendar

import EventIcon from '@mui/icons-material/Event';
import { useUser } from './useUserHook';

import { useNavigate } from 'react-router-dom'; // Assuming you use react-router for navigation



const navItems = [
  { text: 'Home', icon: <HomeIcon />, path: '/', roles: ['student', 'instructor'] },
  { text: 'Courses', icon: <MenuBookIcon />, path: '/dashboard', roles: ['student', 'instructor'] },
  { text: 'Create Exam', icon: <MenuBookIcon />, path: '/create-exam', roles: ['instructor'] },
  { text: 'Create i Card', icon: <MenuBookIcon />, path: '/icard' , roles: ['student']},
  { text: 'Create Announcement', icon: <CampaignIcon sx={{ color: '#ff6600'}} />, path: '/create-announcement' , roles: ['instructor']},
  { text: 'Create event', icon: <EventIcon sx={{ color: '#ff6600'}} />, path: '/create-event' , roles: ['instructor']}
  // Profile handled separately for menu
];

const HideOnScroll = ({ children }) => {
  const trigger = useScrollTrigger();
  return (
    <Slide appear={false} direction="down" in={!trigger}>
      {children}
    </Slide>
  );
};

const Navbar = () => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [profileAnchorEl, setProfileAnchorEl] = useState(null);
  const { user, error } = useUser();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();

  if (!user) return null;

  
 

  const toggleDrawer = (open) => () => {
    setDrawerOpen(open);
  };

  const allowedNavItems = navItems.filter(item =>
    item.roles.includes(user.role)
  );
  
  

  // Open profile menu
  const handleProfileClick = (event) => {
    setProfileAnchorEl(event.currentTarget);
  };

  // Close profile menu
  const handleProfileClose = () => {
    setProfileAnchorEl(null);
  };

  // Logout function
  const handleLogout = () => {
    // Clear user session/localStorage etc
    // For example:
    localStorage.removeItem('authToken');
    // Redirect to login or home page
    navigate('/login');
    handleProfileClose();
  };

  // Handle navigation on clicking nav buttons
  const handleNavClick = (path) => {
    navigate(path);
  };

  // Handle search enter key to navigate/search page
  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      // Redirect to courses with search param or handle search logic
      navigate(`/courses?search=${encodeURIComponent(searchTerm)}`);
    }
  };

  return (
    <>
      <HideOnScroll>
        <AppBar
          position="fixed"
          elevation={0}
          sx={{
            background: 'rgba(0, 0, 0, 0.7)',
            backdropFilter: 'blur(14px)',
            borderBottom: '1px solid rgba(255, 102, 0, 0.3)',
            zIndex: 1300,
          }}
        >
          <Toolbar>
            <Typography
              variant="h6"
              sx={{
                flexGrow: 1,
                fontWeight: 'bold',
                color: '#ff6600',
                textShadow: '0 0 4px rgba(255, 102, 0, 0.5)',
                cursor: 'pointer',
              }}
              onClick={() => navigate('/')}
            >
              MBS E-LEARNING
            </Typography>

            {!isMobile && (
              <>
                

                {/* Navigation Buttons */}
                {allowedNavItems.map((item) => (
                  <Button
                    key={item.text}
                    startIcon={item.icon}
                    sx={{
                      color: '#ff6600',
                      borderRadius: '20px',
                      textTransform: 'none',
                      mx: 1,
                      '&:hover': {
                        backgroundColor: 'rgba(255, 102, 0, 0.1)',
                      },
                    }}
                    onClick={() => handleNavClick(item.path)}
                  >
                    {item.text}
                  </Button>
                ))}

                {/* Profile button with submenu */}
                <Button
                  startIcon={<AccountCircleIcon />}
                  sx={{
                    color: '#ff6600',
                    borderRadius: '20px',
                    textTransform: 'none',
                    mx: 1,
                    '&:hover': {
                      backgroundColor: 'rgba(255, 102, 0, 0.1)',
                    },
                  }}
                  onClick={handleProfileClick}
                >
                  Profile
                </Button>

                <Menu
                  anchorEl={profileAnchorEl}
                  open={Boolean(profileAnchorEl)}
                  onClose={handleProfileClose}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                >
                  <MenuItem
                    onClick={() => {
                      navigate('/profile');
                      handleProfileClose();
                    }}
                  >
                    My Profile
                  </MenuItem>
                  <Divider />
                  <MenuItem onClick={handleLogout}>Logout</MenuItem>
                </Menu>
              </>
            )}

            {isMobile && (
              <IconButton onClick={toggleDrawer(true)} sx={{ color: '#ff6600' }}>
                <MenuIcon />
              </IconButton>
            )}
          </Toolbar>
        </AppBar>
      </HideOnScroll>

      {/* Drawer for mobile */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={toggleDrawer(false)}
        PaperProps={{
          sx: {
            width: 240,
            background: 'rgba(20, 20, 30, 0.85)',
            backdropFilter: 'blur(12px)',
            color: '#fff',
          },
        }}
      >
        <Box sx={{ p: 2 }}>
          <Typography variant="h6" sx={{ color: '#ff6600', mb: 2 }}>
            Navigation
          </Typography>
          <Divider sx={{ borderColor: 'rgba(255, 102, 0, 0.3)' }} />
          <List>
            {/* Search box inside drawer */}
            <ListItem>
              
            </ListItem>

            {/* Nav items */}
            {allowedNavItems.map((item) => (
              <ListItem
                button
                key={item.text}
                onClick={() => {
                  handleNavClick(item.path);
                  setDrawerOpen(false);
                }}
              >
                <ListItemIcon sx={{ color: '#ff6600' }}>{item.icon}</ListItemIcon>
                <ListItemText sx={{ color: '#ff6600' }}>{item.text}</ListItemText>
              </ListItem>
            ))}

            {/* Profile menu item */}
            <ListItem button onClick={handleProfileClick}>
              <ListItemIcon sx={{ color: '#ff6600' }}>
                <AccountCircleIcon />
              </ListItemIcon>
              <ListItemText sx={{ color: '#ff6600' }}>Profile</ListItemText>
            </ListItem>

            {/* Profile submenu inside Drawer */}
            <Menu
              anchorEl={profileAnchorEl}
              open={Boolean(profileAnchorEl)}
              onClose={handleProfileClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
            >
              <MenuItem
                onClick={() => {
                  navigate('/profile');
                  handleProfileClose();
                  setDrawerOpen(false);
                }}
              >
                My Profile
              </MenuItem>
              <Divider />
              <MenuItem
                onClick={() => {
                  handleLogout();
                  setDrawerOpen(false);
                }}
              >
                Logout
              </MenuItem>
            </Menu>
          </List>
        </Box>
      </Drawer>
    </>
  );
};

export default Navbar;
